// Rule renderer for leaves and at-rule bucket grouping.
// Adjacent leaves with same at-rule chain are wrapped together.
import { ensureSemicolon, formatRenderedDeclaration, indent } from '../utils'
import type { LuisRenderLeaf, LuisRenderLeafRule } from './types'

// Internal adjacent at-rule grouping state used only during one render pass.
type RenderBucket = {
  _atRulesKey: string
  _atRules: string[]
  _rules: string[]
}

// Render one selector leaf into CSS rule block text.
export function renderLeafRule(leaf: LuisRenderLeaf): string {
  return [
    `${leaf.selector} {`,
    ...leaf.declarations.map((declaration) => `  ${formatRenderedDeclaration(declaration)}`),
    '}',
  ].join('\n')
}

// Render full CSS rules from root declarations and leaf rules.
export function renderCssFromLeaves(
  rootDeclarations: string[],
  leaves: LuisRenderLeaf[],
  ruleOverridesByKey?: Map<string, string>,
): string[] {
  const output: string[] = []
  const leafRules: LuisRenderLeafRule[] = []

  // Emit preserved top-level at-rule declarations first.
  for (const declaration of rootDeclarations) {
    output.push(ensureSemicolon(declaration))
  }

  // Build leaf rule list with optional pre-rendered rule overrides.
  for (const leaf of leaves) {
    const overrideRule = ruleOverridesByKey?.get(leaf.key)
    leafRules.push({
      key: leaf.key,
      atRules: [...leaf.atRules],
      rule: overrideRule ?? renderLeafRule(leaf),
    })
  }

  // Return declarations plus rendered bucket blocks.
  return [...output, ...renderBuckets(leafRules)]
}

// Render grouped leaves so repeated at-rule wrappers collapse into one block.
function renderBuckets(leaves: LuisRenderLeafRule[]): string[] {
  const buckets: RenderBucket[] = []

  // Group only adjacent leaves that share the same at-rule chain.
  for (const leaf of leaves) {
    const atRulesKey = leaf.atRules.join('\u001f')
    const lastBucket = buckets[buckets.length - 1]
    if (lastBucket && lastBucket._atRulesKey === atRulesKey) {
      lastBucket._rules.push(leaf.rule)
      continue
    } else {
      // Create a new bucket for a new at-rule chain.
    }

    buckets.push({
      _atRulesKey: atRulesKey,
      _atRules: [...leaf.atRules],
      _rules: [leaf.rule],
    })
  }

  const output: string[] = []
  // Render each bucket and wrap grouped rules with its at-rule chain.
  for (const bucket of buckets) {
    if (bucket._atRules.length === 0) {
      output.push(...bucket._rules)
      continue
    } else {
      // Wrap bucket with nested at-rules.
    }

    const block = bucket._rules.join('\n\n')
    output.push(wrapWithAtRules(block, bucket._atRules))
  }

  // Return rendered rule buckets in source order.
  return output
}

// Wrap one rendered rule block with nested at-rules (inside-out).
function wrapWithAtRules(rule: string, atRules: string[]): string {
  return atRules.reduceRight((content, atRule) => {
    return `${atRule} {\n${indent(content)}\n}`
  }, rule)
}
